// Community forum: categories, limits and types shared by the server and the browser.

export type CommunityCategory = {
  id: string;
  label: string;
  description: string;
  /** Only the Cleanor team can start threads here (news). */
  staffOnly?: boolean;
};

export const COMMUNITY_CATEGORIES: CommunityCategory[] = [
  {
    id: 'help',
    label: 'Help with a tool',
    description: 'Stuck on a file, a setting or a result? Ask, and we answer within a day.',
  },
  {
    id: 'bugs',
    label: 'Bug reports',
    description:
      'Something broke or looks wrong. Tell us the tool, the file type and what you saw.',
  },
  {
    id: 'ideas',
    label: 'Ideas and requests',
    description: 'A tool you wish existed, or a feature an existing one is missing.',
  },
  {
    id: 'showcase',
    label: 'Show and tell',
    description: 'Something you made or fixed with Cleanor tools.',
  },
  {
    id: 'news',
    label: 'News',
    description: 'Announcements from the Cleanor team: new tools, fixes and data studies.',
    staffOnly: true,
  },
];

export const COMMUNITY_LIMITS = {
  titleMin: 8,
  titleMax: 140,
  bodyMin: 15,
  bodyMax: 8000,
  replyMin: 2,
  replyMax: 8000,
};

export function getCommunityCategory(id: string | null | undefined) {
  return COMMUNITY_CATEGORIES.find((category) => category.id === id) ?? null;
}

export function communitySlug(title: string) {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 70)
    .replace(/-+$/g, '');
  return slug || 'thread';
}

export function threadPath(thread: { id: string; slug: string }) {
  return `/community/t/${thread.id}/${thread.slug}`;
}

export type CommunityThreadSummary = {
  id: string;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  authorName: string;
  authorIsStaff: boolean;
  toolSlug: string | null;
  replyCount: number;
  staffReplied: boolean;
  answered: boolean;
  createdAt: string;
  lastActivityAt: string;
};

export type CommunityPost = {
  id: string;
  body: string;
  authorName: string;
  authorIsStaff: boolean;
  createdAt: string;
  isAccepted: boolean;
};

export type CommunityThread = CommunityThreadSummary & {
  body: string;
  uid: string;
  acceptedPostId: string | null;
  posts: CommunityPost[];
};

export type CommunitySubmitResult =
  | { ok: true; status: 'published' | 'pending'; path: string; message: string }
  | { ok: false; message: string };
